import { KeyboardEvent, useEffect, useId, useState } from "react";
import {
  AddressField,
  FieldInput,
  SuggestionsList,
  SuggestionItem,
  SuggestionAddress,
  SuggestionDetail,
} from "./Styles";
import { useAddressSuggestions } from "../../../Common/Hooks/useAddressSuggestions";
import { AddressSuggestion } from "../../../Common/Types-DTOs/Address";

interface AddressInputProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (suggestion: AddressSuggestion) => void;
}

/**
 * A plain text input that also suggests real addresses while the user types.
 * Picking one is optional: whatever is typed is kept as it is.
 */
export const AddressInput = ({ value, onChange, onSelect }: AddressInputProps) => {
  // Only what the user types is looked up — not a value loaded from the
  // contract, nor the address a pick just wrote into the field.
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const suggestions = useAddressSuggestions(query);
  const listId = useId();
  const showList = isOpen && suggestions.length > 0;

  useEffect(() => {
    setHighlighted(-1);
  }, [suggestions]);

  const pick = (suggestion: AddressSuggestion) => {
    onSelect(suggestion);
    setQuery("");
    setIsOpen(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setIsOpen(false);
      return;
    }
    if (!showList) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === "Enter" && highlighted >= 0) {
      e.preventDefault();
      pick(suggestions[highlighted]);
    }
  };

  return (
    <AddressField>
      <FieldInput
        type="text"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={showList}
        aria-controls={listId}
        aria-activedescendant={showList && highlighted >= 0 ? `${listId}-${highlighted}` : undefined}
        autoComplete="off"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setQuery(e.target.value);
          setIsOpen(true);
        }}
        onKeyDown={handleKeyDown}
        onBlur={() => setIsOpen(false)}
      />
      {showList && (
        <SuggestionsList id={listId} role="listbox">
          {suggestions.map((suggestion, index) => (
            <SuggestionItem
              key={`${suggestion.address}-${suggestion.postalCode}`}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={index === highlighted}
              $highlighted={index === highlighted}
              // Keeps the input focused, so its blur doesn't close the list before the click lands
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(suggestion)}
            >
              <SuggestionAddress>{suggestion.address}</SuggestionAddress>
              <SuggestionDetail>
                {[suggestion.postalCode, suggestion.province].filter(Boolean).join(" · ")}
              </SuggestionDetail>
            </SuggestionItem>
          ))}
        </SuggestionsList>
      )}
    </AddressField>
  );
};
